/**
 * @file SelectComponent.tsx
 * @description
 * -----------------------------------------------------
 * Componente de Select (con Radix UI)
 * -----------------------------------------------------
 * @version 0.0.2a
 * @created 2025-02-27
 * @modified 2026-04-18
 * @author DLTCode
 * @author Eva Tellez
 * @author Diego Sánchez
 * @author Ismael Benabdellah
 * @author Pedro Luis Gómez
 * @author Nacho Moya
 * @author Ainhoa García
 * @copyright © DLTCode
 * @license Private
 * */

"use client";

import { c as _c } from "react/compiler-runtime";
import GenericTooltip from "@/components/ui/GenericTooltip";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { Select } from "radix-ui";
import clsx from "clsx";
import usePreventScrollLock from "@/hooks/use-prevent-scroll-lock";
import { useId, useRef, useState } from "react";

// Valor centinela interno para representar el string vacío en Radix UI Select
const EMPTY_SENTINEL = "__select_empty__";

// Convierte valores externos a valores internos de Radix (sin strings vacíos)
const toRadixValue = (val: string): string => val === "" ? EMPTY_SENTINEL : val;

// Convierte valores internos de Radix al valor externo original
const fromRadixValue = (val: string): string => val === EMPTY_SENTINEL ? "" : val;
export interface Option {
  readonly value?: string | number;
  readonly label: string;
  readonly icon?: React.ReactNode;
  className?: string;
}
interface SelectComponentCustomProps {
  readonly id?: string;
  readonly name?: string;
  readonly label?: string;
  readonly value: string | number | undefined;
  readonly options: Option[];
  readonly placeholder?: string;
  onChange?: (_e: React.ChangeEvent<HTMLSelectElement>) => void;
  error?: string;
  needLabel?: boolean;
  noTranslation?: boolean;
  className?: string;
  displayMode?: boolean;
  displayText?: string;
  onDisplayClick?: () => void;
  isExpanded?: boolean;
  badgeClassName?: string;
  ariaLabel?: string;
  onSelect?: (_value: string) => void;
  selectedValue?: string | number;
  isRequired?: boolean;
  disabled?: boolean;
  useDefaultPlaceholder?: boolean;
  placeholderValue?: string;
  clearable?: boolean;
  tooltip?: {
    title?: string;
    content: React.ReactNode;
  };
  style?: React.CSSProperties;
  disableAutoAriaLabelFallback?: boolean;
  suppressAriaLabel?: boolean;
}
const SelectComponent = t0 => {
  const $ = _c(95);
  const {
    id,
    name,
    label,
    value,
    options,
    placeholder,
    onChange,
    onSelect,
    selectedValue,
    error,
    ariaLabel,
    needLabel: t1,
    noTranslation: t2,
    isRequired: t3,
    className: t4,
    disabled: t5,
    useDefaultPlaceholder: t6,
    tooltip,
    placeholderValue: t7,
    clearable: t8,
    style,
    disableAutoAriaLabelFallback: t9,
    suppressAriaLabel: t10
  } = t0;
  const needLabel = t1 === undefined ? true : t1;
  const noTranslation = t2 === undefined ? false : t2;
  const isRequired = t3 === undefined ? false : t3;
  const className = t4 === undefined ? "" : t4;
  const disabled = t5 === undefined ? false : t5;
  const useDefaultPlaceholder = t6 === undefined ? true : t6;
  const placeholderValue = t7 === undefined ? "" : t7;
  const clearable = t8 === undefined ? false : t8;
  const disableAutoAriaLabelFallback = t9 === undefined ? false : t9;
  const suppressAriaLabel = t10 === undefined ? false : t10;
  const [isOpen, setIsOpen] = useState(false);
  const generatedId = useId();
  const triggerRef = useRef(null);
  const currentValue = value !== undefined ? value : selectedValue;
  const isDisabled = disabled || options.length === 1;
  const normalizedCurrentValue = currentValue === undefined ? String(placeholderValue) : String(currentValue);
  const defaultPlaceholder = useDefaultPlaceholder ? "Seleccionar" : "";
  const placeholderLabel = placeholder || defaultPlaceholder;
  let t11;
  if ($[0] !== generatedId || $[1] !== id || $[2] !== name) {
    t11 = id || name || `select-${generatedId.replace(/[:]/g, "")}`;
    $[0] = generatedId;
    $[1] = id;
    $[2] = name;
    $[3] = t11;
  } else {
    t11 = $[3];
  }
  const stableIdBase = t11;
  const contentId = `${stableIdBase}-content`;
  const labelId = `${stableIdBase}-label`;
  const hasVisibleLabel = Boolean(needLabel && label);
  let t12;
  if ($[4] !== ariaLabel || $[5] !== label) {
    t12 = ariaLabel?.trim() || label?.trim();
    $[4] = ariaLabel;
    $[5] = label;
    $[6] = t12;
  } else {
    t12 = $[6];
  }
  const explicitAriaLabel = t12;
  let t13;
  if ($[7] !== id || $[8] !== name || $[9] !== placeholderLabel) {
    t13 = placeholderLabel?.trim() || name || id || "Select";
    $[7] = id;
    $[8] = name;
    $[9] = placeholderLabel;
    $[10] = t13;
  } else {
    t13 = $[10];
  }
  const fallbackAriaLabel = t13;
  const resolvedAriaLabel = disableAutoAriaLabelFallback ? explicitAriaLabel : explicitAriaLabel || fallbackAriaLabel;
  const triggerAriaLabel = suppressAriaLabel || hasVisibleLabel ? undefined : resolvedAriaLabel;
  let t14;
  if ($[11] !== clearable || $[12] !== options || $[13] !== placeholderLabel) {
    t14 = clearable && placeholderLabel ? [{
      value: "",
      label: placeholderLabel
    } as Option, ...options] : options;
    $[11] = clearable;
    $[12] = options;
    $[13] = placeholderLabel;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  const allOptions = t14;
  let t15;
  if ($[15] !== allOptions || $[16] !== normalizedCurrentValue) {
    let t16;
    if ($[18] !== normalizedCurrentValue) {
      t16 = opt => {
        const optVal = opt.value !== undefined ? String(opt.value) : "";
        return optVal === normalizedCurrentValue;
      };
      $[18] = normalizedCurrentValue;
      $[19] = t16;
    } else {
      t16 = $[19];
    }
    t15 = allOptions.some(t16);
    $[15] = allOptions;
    $[16] = normalizedCurrentValue;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  const hasMatchingOption = t15;
  let t16;
  if ($[20] !== hasMatchingOption || $[21] !== normalizedCurrentValue) {
    t16 = !hasMatchingOption ? undefined : toRadixValue(normalizedCurrentValue);
    $[20] = hasMatchingOption;
    $[21] = normalizedCurrentValue;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  const radixValue = t16;
  let t17;
  if ($[23] !== id || $[24] !== name || $[25] !== onChange || $[26] !== onSelect) {
    t17 = newValue => {
      const externalValue = fromRadixValue(newValue);
      if (onChange) {
        const syntheticEvent = {
          target: {
            id: id || name || "",
            name: name || id || "",
            value: externalValue
          }
        } as React.ChangeEvent<HTMLSelectElement>;
        onChange(syntheticEvent);
      }
      if (onSelect) {
        onSelect(externalValue);
      }
    };
    $[23] = id;
    $[24] = name;
    $[25] = onChange;
    $[26] = onSelect;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  const handleValueChange = t17;
  let t18;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = direction => {
      const trigger = triggerRef.current;
      if (!trigger) {
        return;
      }
      const focusScope = trigger.closest("form") ?? trigger.closest(".modal__body") ?? document.body;
      const focusableSelector = ["a[href]", "button:not([disabled])", "input:not([disabled])", "select:not([disabled])", "textarea:not([disabled])", "[tabindex]:not([tabindex='-1'])"].join(", ");
      const focusables = Array.from(focusScope.querySelectorAll(focusableSelector)).filter(_temp);
      const currentIndex = focusables.findIndex(element_0 => element_0 === trigger);
      if (currentIndex < 0) {
        return;
      }
      const targetIndex = direction === "next" ? currentIndex + 1 : currentIndex - 1;
      focusables[targetIndex]?.focus();
    };
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  const focusAdjacentFocusableElement = t18;
  let t19;
  if ($[29] !== isOpen) {
    t19 = event => {
      if (event.key !== "Tab" || !isOpen) {
        return;
      }
      const content = event.currentTarget;
      const options_0 = Array.from(content.querySelectorAll("[role='option']:not([data-disabled])"));
      if (!options_0.length) {
        return;
      }
      const activeOption = (document.activeElement as HTMLElement | null)?.closest("[role='option']") as HTMLElement | null;
      const currentIndex_0 = activeOption ? options_0.indexOf(activeOption) : -1;
      const nextIndex = event.shiftKey ? currentIndex_0 - 1 : currentIndex_0 + 1;
      if (nextIndex >= 0 && nextIndex < options_0.length) {
        event.preventDefault();
        options_0[nextIndex]?.focus();
        return;
      }
      event.preventDefault();
      setIsOpen(false);
      requestAnimationFrame(() => {
        focusAdjacentFocusableElement(event.shiftKey ? "prev" : "next");
      });
    };
    $[29] = isOpen;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  const handleContentKeyDown = t19;
  let t20;
  if ($[31] !== noTranslation) {
    t20 = option => {
      if (noTranslation) {
        return option.label !== "select" ? option.label || "Seleccionar" : "Seleccionar";
      }
      return option.label;
    };
    $[31] = noTranslation;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  const getOptionLabel = t20;
  usePreventScrollLock();
  const t21 = `input__group ${className}`;
  let t22;
  if ($[33] !== error || $[34] !== id || $[35] !== isRequired || $[36] !== label || $[37] !== labelId || $[38] !== needLabel || $[39] !== tooltip) {
    t22 = needLabel && label && <label id={labelId} className={`label ${error ? "label--error" : ""}`} htmlFor={id}>{label}{isRequired && "*"}{tooltip && <GenericTooltip title={tooltip.title}>{tooltip.content}</GenericTooltip>}</label>;
    $[33] = error;
    $[34] = id;
    $[35] = isRequired;
    $[36] = label;
    $[37] = labelId;
    $[38] = needLabel;
    $[39] = tooltip;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  const t23 = name || id;
  const t24 = currentValue || "";
  let t25;
  if ($[41] !== t23 || $[42] !== t24) {
    t25 = <input type="hidden" name={t23} value={t24} />;
    $[41] = t23;
    $[42] = t24;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  const t26 = `select-group__wrapper ${className}`;
  const t27 = isRequired || undefined;
  const t28 = (!hasMatchingOption || clearable && normalizedCurrentValue === String(placeholderValue)) && "select__placeholder";
  const t29 = error && "select__error";
  const t30 = isDisabled && "select__disabled";
  let t31;
  if ($[44] !== className || $[45] !== t28 || $[46] !== t29 || $[47] !== t30) {
    t31 = clsx("select", t28, t29, t30, className);
    $[44] = className;
    $[45] = t28;
    $[46] = t29;
    $[47] = t30;
    $[48] = t31;
  } else {
    t31 = $[48];
  }
  let t32;
  if ($[49] !== placeholderLabel) {
    t32 = <p><Select.Value placeholder={placeholderLabel} /></p>;
    $[49] = placeholderLabel;
    $[50] = t32;
  } else {
    t32 = $[50];
  }
  let t33;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Select.Icon><ChevronDownIcon className="select__chevron" /></Select.Icon>;
    $[51] = t33;
  } else {
    t33 = $[51];
  }
  let t34;
  if ($[52] !== contentId || $[53] !== id || $[54] !== t27 || $[55] !== t31 || $[56] !== t32 || $[57] !== triggerAriaLabel) {
    t34 = <Select.Trigger id={id} ref={triggerRef} aria-label={triggerAriaLabel} aria-controls={contentId} aria-required={t27} className={t31}>{t32}{t33}</Select.Trigger>;
    $[52] = contentId;
    $[53] = id;
    $[54] = t27;
    $[55] = t31;
    $[56] = t32;
    $[57] = triggerAriaLabel;
    $[58] = t34;
  } else {
    t34 = $[58];
  }
  let t35;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <Select.ScrollUpButton className="select__scroll-button"><ChevronUpIcon /></Select.ScrollUpButton>;
    $[59] = t35;
  } else {
    t35 = $[59];
  }
  let t36;
  if ($[60] !== allOptions || $[61] !== getOptionLabel) {
    let t37;
    if ($[63] !== getOptionLabel) {
      t37 = (option_0, index) => {
        const optValue = toRadixValue(option_0.value !== undefined ? String(option_0.value) : `option-${index}`);
        return <Select.Item key={optValue} value={optValue} className={clsx("select__dropdown__list--item", option_0.className)}><span className="select__option-content">{option_0.icon ? <span className="select__option-icon" aria-hidden="true">{option_0.icon}</span> : null}<Select.ItemText><span className="select__option-text">{getOptionLabel(option_0)}</span></Select.ItemText></span></Select.Item>;
      };
      $[63] = getOptionLabel;
      $[64] = t37;
    } else {
      t37 = $[64];
    }
    t36 = allOptions.map(t37);
    $[60] = allOptions;
    $[61] = getOptionLabel;
    $[62] = t36;
  } else {
    t36 = $[62];
  }
  let t37;
  if ($[65] !== t36) {
    t37 = <Select.Viewport className="select__dropdown__list">{t36}</Select.Viewport>;
    $[65] = t36;
    $[66] = t37;
  } else {
    t37 = $[66];
  }
  let t38;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <Select.ScrollDownButton className="select__scroll-button"><ChevronDownIcon /></Select.ScrollDownButton>;
    $[67] = t38;
  } else {
    t38 = $[67];
  }
  let t39;
  if ($[68] !== contentId || $[69] !== handleContentKeyDown || $[70] !== t37) {
    t39 = <Select.Portal><Select.Content id={contentId} className="select__dropdown" position="popper" sideOffset={4} onKeyDown={handleContentKeyDown}>{t35}{t37}{t38}</Select.Content></Select.Portal>;
    $[68] = contentId;
    $[69] = handleContentKeyDown;
    $[70] = t37;
    $[71] = t39;
  } else {
    t39 = $[71];
  }
  let t40;
  if ($[72] !== contentId || $[73] !== isOpen) {
    t40 = !isOpen && <span id={contentId} hidden={true} aria-hidden="true" />;
    $[72] = contentId;
    $[73] = isOpen;
    $[74] = t40;
  } else {
    t40 = $[74];
  }
  let t41;
  if ($[75] !== handleValueChange || $[76] !== isDisabled || $[77] !== isOpen || $[78] !== radixValue || $[79] !== t34 || $[80] !== t39 || $[81] !== t40) {
    t41 = <Select.Root open={isOpen} onOpenChange={setIsOpen} value={radixValue} onValueChange={handleValueChange} disabled={isDisabled}>{t34}{t39}{t40}</Select.Root>;
    $[75] = handleValueChange;
    $[76] = isDisabled;
    $[77] = isOpen;
    $[78] = radixValue;
    $[79] = t34;
    $[80] = t39;
    $[81] = t40;
    $[82] = t41;
  } else {
    t41 = $[82];
  }
  let t42;
  if ($[83] !== t26 || $[84] !== t41) {
    t42 = <section className={t26}>{t41}</section>;
    $[83] = t26;
    $[84] = t41;
    $[85] = t42;
  } else {
    t42 = $[85];
  }
  let t43;
  if ($[86] !== error) {
    t43 = error && <span className="input-group__error-message">{error}</span>;
    $[86] = error;
    $[87] = t43;
  } else {
    t43 = $[87];
  }
  let t44;
  if ($[88] !== style || $[89] !== t21 || $[90] !== t22 || $[91] !== t25 || $[92] !== t42 || $[93] !== t43) {
    t44 = <article className={t21} style={style}>{t22}{t25}{t42}{t43}</article>;
    $[88] = style;
    $[89] = t21;
    $[90] = t22;
    $[91] = t25;
    $[92] = t42;
    $[93] = t43;
    $[94] = t44;
  } else {
    t44 = $[94];
  }
  return t44;
};
export default SelectComponent;
function _temp(element) {
  if (element.getAttribute("aria-hidden") === "true") {
    return false;
  }
  return element.offsetParent !== null || element === document.activeElement;
}